import Decimal from "decimal.js";
import { makerEnvironmentMatches } from "../environment";

export interface MakerObservation {
  maker_id: string;
  wallets: string[];
  enabled: boolean;
  membership_version: number;
  expected_checks: number;
  observed_checks: number;
  missing_checks: number;
  status:
    | "observed"
    | "partial"
    | "unavailable"
    | "not_observed"
    | "not_enrolled_during_hour";
  points: { PERPS: string; OPTIONS: string };
  commitments: {
    market: string;
    slot: number | null;
    expected_checks: number;
    valid_checks: number;
    passed_checks: number;
    gap_checks: number;
    volatile_checks: number;
    passed_volatile_checks: number;
    agreed_hour: boolean;
    assessment: "meeting" | "below" | "unavailable" | "outside_agreed_hours";
  }[];
  markets: {
    market: string;
    product: string;
    points: string;
    valid_checks: number;
    gap_checks: number;
    meeting_checks: number;
    latest_quote?: {
      bid_depth_usd: string | null;
      ask_depth_usd: string | null;
      bid_distance: string | null;
      ask_distance: string | null;
      distance_unit: "bps" | "vol_points";
      minimum_side_usd: string | null;
      bid_within_band: boolean;
      ask_within_band: boolean;
      reason: string;
    };
  }[];
  reasons: string[];
}
export interface MakerObservability {
  schema_version: number;
  environment: string;
  testnet_only: boolean;
  server_time: string;
  policy_version: string;
  policy_effective_at: string | null;
  reward_effective_at: string | null;
  start: string;
  end: string;
  expected_checks: number;
  observed_checks: number;
  missing_checks: number;
  gap_checks: number;
  status: "observed" | "partial" | "unavailable" | "not_configured";
  reasons: string[];
  collector: {
    status: "fresh" | "stale" | "unavailable";
    latest_check_at: string | null;
    latest_reference_at: string | null;
    checked_at: string;
    stale_after_seconds: number;
  };
  current: {
    at: string | null;
    status: "observed" | "partial" | "unavailable";
    reasons: string[];
    makers: MakerObservation[];
  };
  makers: MakerObservation[];
  computed_at: string;
}
export const validDate = (v: unknown): v is string =>
  typeof v === "string" && Number.isFinite(Date.parse(v));
const count = (v: unknown): v is number =>
  typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
const strings = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === "string");
const PreciseDecimal = Decimal.clone({ precision: 100 });
export function pointValue(raw: string): Decimal | undefined {
  if (
    typeof raw !== "string" ||
    !/^\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)?$/.test(raw)
  )
    return undefined;
  try {
    const [numerator, denominator = "1"] = raw.split("/");
    if (new PreciseDecimal(denominator).isZero()) return undefined;
    const value = new PreciseDecimal(numerator).div(denominator);
    return value.isFinite() && value.gte(0) ? value : undefined;
  } catch {
    return undefined;
  }
}
export function formatPoints(raw: string) {
  const value = pointValue(raw);
  if (!value) return "Unavailable";
  if (value.gt(0) && value.lt("0.01")) return "<0.01";
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
export function verifyObservability(data: MakerObservability) {
  const invalid = () => {
    throw new Error(
      "Monitoring response could not be verified for this environment. Refresh or contact engineering."
    );
  };
  if (
    !data ||
    !makerEnvironmentMatches(data) ||
    data.schema_version !== 1 ||
    ![data.server_time, data.start, data.end, data.computed_at].every(
      validDate
    ) ||
    ![data.policy_effective_at, data.reward_effective_at].every(
      (v) => v === null || validDate(v)
    ) ||
    Date.parse(data.end) - Date.parse(data.start) !== 3600000 ||
    Date.parse(data.start) % 3600000 !== 0 ||
    Date.parse(data.end) > Date.parse(data.server_time) ||
    typeof data.policy_version !== "string" ||
    ![
      data.expected_checks,
      data.observed_checks,
      data.missing_checks,
      data.gap_checks,
    ].every(count) ||
    data.expected_checks !== 360 ||
    data.observed_checks + data.missing_checks !== data.expected_checks ||
    data.gap_checks > data.observed_checks ||
    !["observed", "partial", "unavailable", "not_configured"].includes(
      data.status
    ) ||
    !strings(data.reasons) ||
    !data.collector ||
    !["fresh", "stale", "unavailable"].includes(data.collector.status) ||
    !validDate(data.collector.checked_at) ||
    !count(data.collector.stale_after_seconds) ||
    ![data.collector.latest_check_at, data.collector.latest_reference_at].every(
      (v) => v === null || validDate(v)
    ) ||
    !data.current ||
    !["observed", "partial", "unavailable"].includes(data.current.status) ||
    !(data.current.at === null || validDate(data.current.at)) ||
    !strings(data.current.reasons) ||
    !Array.isArray(data.current.makers) ||
    !Array.isArray(data.makers)
  )
    invalid();
  const ids = new Set<string>();
  [...data.makers, ...data.current.makers].forEach((m, index) => {
    if (index === data.makers.length) ids.clear();
    if (
      !m ||
      typeof m.maker_id !== "string" ||
      !m.maker_id ||
      ids.has(m.maker_id) ||
      !strings(m.wallets) ||
      typeof m.enabled !== "boolean" ||
      !count(m.membership_version) ||
      ![m.expected_checks, m.observed_checks, m.missing_checks].every(count) ||
      m.observed_checks + m.missing_checks !== m.expected_checks ||
      ![
        "observed",
        "partial",
        "unavailable",
        "not_observed",
        "not_enrolled_during_hour",
      ].includes(m.status) ||
      !strings(m.reasons) ||
      !m.points ||
      !pointValue(m.points.PERPS) ||
      !pointValue(m.points.OPTIONS) ||
      !Array.isArray(m.commitments) ||
      !Array.isArray(m.markets)
    )
      invalid();
    ids.add(m.maker_id);
    m.commitments.forEach((c) => {
      if (
        !c ||
        typeof c.market !== "string" ||
        !(c.slot === null || count(c.slot)) ||
        ![
          c.expected_checks,
          c.valid_checks,
          c.passed_checks,
          c.gap_checks,
          c.volatile_checks,
          c.passed_volatile_checks,
        ].every(count) ||
        c.passed_checks > c.valid_checks ||
        c.passed_volatile_checks > c.volatile_checks ||
        c.volatile_checks > c.valid_checks ||
        typeof c.agreed_hour !== "boolean" ||
        !["meeting", "below", "unavailable", "outside_agreed_hours"].includes(
          c.assessment
        )
      )
        invalid();
    });
    m.markets.forEach((market) => {
      if (
        !market ||
        typeof market.market !== "string" ||
        !["PERPS", "OPTIONS"].includes(market.product) ||
        !pointValue(market.points) ||
        ![market.valid_checks, market.gap_checks, market.meeting_checks].every(
          count
        ) ||
        market.meeting_checks > market.valid_checks ||
        (market.latest_quote !== undefined &&
          (!market.latest_quote ||
            ![
              market.latest_quote.bid_depth_usd,
              market.latest_quote.ask_depth_usd,
              market.latest_quote.minimum_side_usd,
            ].every((v) => v === null || !!pointValue(v)) ||
            ![
              market.latest_quote.bid_distance,
              market.latest_quote.ask_distance,
            ].every(
              (v) =>
                v === null ||
                (typeof v === "string" && !!pointValue(v.replace(/^-/, "")))
            ) ||
            !["bps", "vol_points"].includes(
              market.latest_quote.distance_unit
            ) ||
            typeof market.latest_quote.bid_within_band !== "boolean" ||
            typeof market.latest_quote.ask_within_band !== "boolean" ||
            typeof market.latest_quote.reason !== "string"))
      )
        invalid();
    });
  });
  return data;
}
export function needsAttention(maker: MakerObservation) {
  return (
    maker.enabled &&
    (maker.status !== "observed" ||
      maker.commitments.length === 0 ||
      maker.missing_checks > 0 ||
      maker.reasons.length > 0 ||
      maker.commitments.some(
        (c) =>
          c.assessment === "below" ||
          c.assessment === "unavailable" ||
          c.gap_checks > 0
      ))
  );
}
